import { useEffect, useRef, useState } from "react";
import type { Locale } from "@tovi/shared";
import { Button, LocaleSwitch, QrCode, useTranslate } from "@tovi/ui";
import { setToken } from "@/lib/api.ts";
import { requestPairingCode, waitForApproval, type PairingCode } from "@/lib/pairing.ts";

/**
 * Signing the **terminal** in (`02-architecture.md` § Auth).
 *
 * Not a staff login. This is the once-per-device step that gets the bearer
 * token; staff identify themselves afterwards, per shift, by scanning the
 * till's QR with their own staff-app credential.
 *
 * The terminal pairs itself by device code (RFC 8628, `lib/pairing.ts`) —
 * there is no other way to set a till up: it shows a code and a QR, a
 * `terminals:pair` holder approves it from the back office's "Pair a till"
 * page, and the till redeems the code for the bearer token.
 *
 * The token goes to `localStorage` here and to `localStorage` in the
 * packaged app too — the seam is `lib/api.ts`, which is the only thing that
 * reads it.
 *
 * It is also the screen a 401 lands on mid-service, and `expired` is the
 * difference. Setting a terminal up and being thrown out of one are the same
 * form and not the same moment: the second needs to say what happened and that
 * nothing has been lost, or a cashier holding a settled bill will assume it
 * has.
 */
export function SignInScreen({
  locale,
  expired = false,
  onLocaleChange,
  onSignedIn,
}: {
  locale: Locale;
  expired?: boolean;
  onLocaleChange: (next: Locale) => void;
  onSignedIn: () => void;
}) {
  const t = useTranslate();

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-4">
        {/* Setting a terminal up is the only moment the device's own language
            is anyone's choice — after this `/me` decides, which is why a till
            being signed back in is not offered the switch: the store has
            already answered, and a control that changes nothing is worse than
            no control. */}
        {expired ? null : (
          <div className="flex justify-end">
            <LocaleSwitch current={locale} onSelect={onLocaleChange} label={t("shell.language")} />
          </div>
        )}

        <h1 className="text-xl font-semibold text-ink-body">
          {t(expired ? "pos.signIn.expiredTitle" : "pos.signIn.title")}
        </h1>
        <p className="text-sm text-ink-muted">
          {t(expired ? "pos.signIn.expiredSubtitle" : "pos.signIn.subtitle")}
        </p>

        <PairingPanel onSignedIn={onSignedIn} />
      </div>
    </div>
  );
}

type PairingStatus = "waiting" | "retrying" | "denied" | "failed";

/** `"WDJBMJHT"` → `"WDJB-MJHT"`, for reading aloud. Better Auth accepts either. */
function groupUserCode(userCode: string): string {
  const midpoint = Math.ceil(userCode.length / 2);
  return `${userCode.slice(0, midpoint)}-${userCode.slice(midpoint)}`;
}

function PairingPanel({ onSignedIn }: { onSignedIn: () => void }) {
  const t = useTranslate();
  const [code, setCode] = useState<PairingCode | null>(null);
  const [status, setStatus] = useState<PairingStatus>("waiting");
  // Bumped to ask the effect below for a fresh code without waiting for a
  // prop or view change — the only other things it already restarts on.
  const [attempt, setAttempt] = useState(0);

  // `onSignedIn` is a fresh closure on every render of `App`. A ref rather
  // than a dependency: an unrelated re-render of `App` — a locale switch, a
  // query settling — must not restart this effect and throw away a code
  // somebody is mid-way through typing into the back office. Written from an
  // effect, not the render body, which the Rules of React forbid touching a
  // ref in.
  const onSignedInRef = useRef(onSignedIn);
  useEffect(() => {
    onSignedInRef.current = onSignedIn;
  });

  useEffect(() => {
    const controller = new AbortController();

    async function pair(): Promise<void> {
      for (;;) {
        let pairingCode: PairingCode;
        try {
          pairingCode = await requestPairingCode(controller.signal);
        } catch {
          if (!controller.signal.aborted) setStatus("failed");
          return;
        }
        if (controller.signal.aborted) return;
        setCode(pairingCode);
        setStatus("waiting");

        const outcome = await waitForApproval(pairingCode, controller.signal, (retrying) =>
          setStatus(retrying ? "retrying" : "waiting"),
        );
        if (controller.signal.aborted) return;

        if (outcome.kind === "approved") {
          setToken(outcome.token);
          onSignedInRef.current();
          return;
        }
        // A till left on this screen overnight must still show a live code in
        // the morning — automatically, and never faster than one code per
        // expiry, since the loop only reaches here once `waitForApproval` has
        // already run the previous code out.
        if (outcome.kind === "expired") continue;

        // Denied and failed both stop here: a refusal is somebody's decision,
        // and a new code is offered rather than fetched on their behalf.
        setStatus(outcome.kind);
        return;
      }
    }

    void pair();
    return () => controller.abort();
  }, [attempt]);

  if (!code) {
    // Requesting the very first code failed outright — there is nothing on
    // screen yet to fall back to, so this is the one place `failed` needs its
    // own rendering rather than sitting below a code.
    if (status === "failed") {
      return (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-danger">{t("pos.signIn.failed")}</p>
          <Button variant="secondary" onClick={() => setAttempt((value) => value + 1)}>
            {t("pos.signIn.newCode")}
          </Button>
        </div>
      );
    }
    return <p className="text-center text-sm text-ink-muted">{t("pos.signIn.waiting")}</p>;
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <QrCode value={code.verificationUriComplete} title={t("pos.signIn.codeLabel")} />
      <p className="text-center font-mono text-2xl font-semibold tracking-widest text-ink-body">
        {groupUserCode(code.userCode)}
      </p>
      <p className="text-center text-sm text-ink-muted">
        {t("pos.signIn.instruction", { uri: code.verificationUri })}
      </p>

      {status === "waiting" ? (
        <p className="text-sm text-ink-muted">{t("pos.signIn.waiting")}</p>
      ) : null}
      {status === "retrying" ? (
        <p className="text-sm text-warning-ink">{t("pos.signIn.retrying")}</p>
      ) : null}
      {status === "denied" || status === "failed" ? (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-danger">
            {t(status === "denied" ? "pos.signIn.denied" : "pos.signIn.failed")}
          </p>
          <Button variant="secondary" onClick={() => setAttempt((value) => value + 1)}>
            {t("pos.signIn.newCode")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
